import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

/**
 * Wave 7 — AI ACTION RECEIPTS + UNDO.
 *
 * A receipt is a user-accepted AI action (a change the AI proposed and the
 * human applied) recorded server-side with before/after state so it can be
 * shown back and reversed. Distinct from `ai_runs` (the inference log).
 */
export interface AiAction {
  id: string;
  workspace_id: string;
  user_id: string | null;
  kind: string;
  agent: string;
  target_kind: string;
  target_id: string;
  summary: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  undone_at: string | null;
  created_at: string;
}

export function useAiActions(wsId: string | undefined, limit = 20) {
  return useQuery<AiAction[]>({
    queryKey: ['ai-actions', wsId, limit],
    enabled: !!wsId,
    queryFn: async () =>
      (await api.get<AiAction[]>(`/workspaces/${wsId}/ai-actions?limit=${limit}`)).data,
  });
}

/** Reverse an AI action. Re-invalidates the feed plus AP invoice queries
 *  (undoing an apply-suggestion changes the invoice's expense account). */
export function useUndoAiAction(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<AiAction, unknown, string>({
    mutationFn: async (actionId: string) =>
      (await api.post<AiAction>(`/workspaces/${wsId}/ai-actions/${actionId}/undo`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-actions', wsId] });
      qc.invalidateQueries({ queryKey: ['ap-invoices', wsId] });
      qc.invalidateQueries({ queryKey: ['ap-invoice', wsId] });
      qc.invalidateQueries({ queryKey: ['inbox-counts', wsId] });
    },
  });
}
